import templateJsonStore from '../components/TemplateJsonStore.js';
import scriptItemStore from '../components/ScriptItemStore.js';
import eventItemStore from '../components/EventItemStore.js';
import appSettingStore from '../components/AppSettingStore.js';
import serviceworkerItemStore from '../components/ServiceWorkerItemStore.js';
import attributeItemStore from '../components/AttributeItemStore.js';
import toolBoxItemStore from '../components/ToolBoxItemStore.js';

import {
    addScriptToServiceWorkerEvent,
    deleteServiceWorkerEvent,
    deleteServiceWorkerEventScript,
    upsertServiceWorkerEvent
} from '../components/ServiceWorkerItemSlice.js';

import {
    addTopLevelPage,
    insertAsChild,
    insertAsLastChild,
    insertAfter,
    updateElementAttribute,
    moveToTrashbox,
    deleteElementPermanently,
    setReloadOff,
    setReloadOn,
    moveAfter,
    moveAsChild,
    moveAsLastChild,
    findElementById,
    findElementByDomId,
    deleteTemplateByAttributeId,
    replaceTemplateChildren,
} from '../components/TemplateJsonSlice.js';

import {
    addNewScript,
    deleteScript,
    updateScriptItem,
    selectScriptByName,
    deleteScriptPermanently
} from '../components/ScriptItemSlice.js';

import {
    addEventToExistingTag,
    addNewTag as addNewEventTag,
    addScriptToExistingTag as addScriptToEvent,
    removeEventFromExistingTag as removeEventDefinition,
    deleteEventScript as removeScriptFromEvent,
    removeEventsByScript,
    selectEventByTagName
} from '../components/EventItemSlice.js';

import {
    updateAppHeader,
    updateBodyBottom,
    updateBodyTop,
    updateProjectSpecification
} from '../components/AppSettingSlice.js';

import {
    syncUserScriptComponent,
    removeUserScriptComponentsByScriptName
} from '../components/ToolBoxItemSlice.js';

import {
    addAttributeToExistingTag,
    addNewTag as addNewAttributeTag,
    selectAttributeByTagName,
    setIdTypeCommentForTag,
    removeAttributesByScript
} from '../components/AttributeItemSlice.js';

import {
    extractDeclarationInfo,
    findCustomElementDefinitions,
    parseJSDoc
} from '../components/ScriptParser.js';

import { mergeCss } from './CssMerger.js';

// 保護対象の定義 (CSSセレクタ形式)
// これらにマッチする要素は削除コマンドを受け付けません
const PROTECTED_RESOURCES = [
    // --- Meta Tags ---
    'meta[charset="UTF-8"]',
    'meta[http-equiv="X-UA-Compatible"]',
    'meta[name="viewport"]',

    // --- Favicons & Touch Icons ---
    'link[rel="icon"][href="favicon.ico"]',
    'link[rel="icon"][href*="images/icon.svg"]',
    'link[rel="apple-touch-icon"][href*="images/apple-touch-icon.webp"]',

    // --- PWA Manifest ---
    'link[rel="manifest"][href*="manifest.webmanifest"]',

    // --- Essential Libraries ---
    'script[src*="holder.min.js"]',                   // holder.js
    'link[href*="fontawesome-free"][href*="all.min.css"]', // fontawesome v6
    'link[href*="onsenui"][href*="onsenui-core.min.css"]', // Onsen UI CSS (core)
    'link[href*="onsenui"][href*="onsen-css-components.min.css"]', // Onsen UI CSS (components)
    'script[src*="onsenui"][src*="onsenui.min.js"]',    // Onsen UI JS
];

// 各Reduxストアのdispatch関数を保持する変数
let storeDispatch, scriptDispatch, eventDispatch, appSettingDispatch, attributeDispatch, toolBoxDispatch, serviceWorkerDispatch;

/**
 * コマンドエンジンの初期化を行います。
 * 各Reduxストアのdispatch関数をエンジンに登録し、コマンド実行可能状態にします。
 * @param {object} stores - 各ストアのインスタンスを含むオブジェクト。
 */
export function initializeCommandEngine(stores) {
    storeDispatch = stores.templateJsonStore.dispatch;
    scriptDispatch = stores.scriptItemStore.dispatch;
    eventDispatch = stores.eventItemStore.dispatch;
    appSettingDispatch = stores.appSettingStore.dispatch;
    attributeDispatch = stores.attributeItemStore.dispatch;
    toolBoxDispatch = stores.toolBoxItemStore.dispatch;
    // Service Workerストアのdispatch関数を登録
    serviceWorkerDispatch = stores.serviceworkerItemStore.dispatch;
}

// ========== 内部ヘルパー関数 ==========

/**
 * HTML文字列に対し、要素の追加または削除を行います。
 * 削除時は PROTECTED_RESOURCES に基づく保護チェックを実行します。
 */
function modifyResourceHtml(htmlString, operation, params) {
    const parser = new DOMParser();
    // head/bodyどちらの断片がきてもいいようにHTML全体としてパース
    const doc = parser.parseFromString(`<html><head>${htmlString || ''}</head><body></body></html>`, 'text/html');
    const container = doc.head; // HTML断片はhead内に展開される仕様を利用

    if (operation === 'add') {
        // --- 追加処理 ---
        const { tagName, attributes, content } = params;
        const newEl = doc.createElement(tagName);

        if (attributes) {
            Object.entries(attributes).forEach(([key, val]) => {
                newEl.setAttribute(key, val);
            });
        }
        if (content) {
            newEl.textContent = content;
        }

        // 末尾に追加 (既存リソースの後ろ)
        container.appendChild(newEl);

    } else if (operation === 'remove') {
        // --- 削除処理 (保護機能付き) ---
        const { selector } = params;
        if (!selector) throw new Error("削除対象のセレクタ(selector)が指定されていません。");

        const targets = container.querySelectorAll(selector);

        // 削除対象が見つからない場合はエラーにせず無視 (冪等性担保)
        if (targets.length > 0) {
            targets.forEach(target => {
                // [SECURITY CHECK] 削除しようとしている要素が保護対象かチェック
                const isProtected = PROTECTED_RESOURCES.some(protectedSelector => {
                    return target.matches(protectedSelector);
                });

                if (isProtected) {
                    // 保護対象の場合はエラーをスローし、変更を一切適用しない
                    throw new Error(
                        `[Security Violation] AI attempted to remove a protected resource: "${target.outerHTML}". ` +
                        `Deletion of system libraries (Onsen UI, etc.) or critical meta tags is strictly prohibited.`
                    );
                }

                // チェックを通過した場合のみ削除
                target.remove();
            });
        }
    }

    return container.innerHTML;
}

/**
 * ストアからID(UUID)で要素を検索 (互換性のため残すが、内部でSliceの関数を呼ぶ)
 */
function findElementInStoreById(id) {
    // TemplateJsonSlice.js の findElementById は state オブジェクト全体 (state.templates, state.trashbox を持つ) を期待する
    const state = templateJsonStore.getState().templateJson;
    // ※ Slice側の findElementById の実装を見ると `state.templates` を直接ループしているが、
    //    呼び出し側が `state` を渡す想定で書かれているため、そのまま渡す。
    return findElementById(state, id);
}

/**
 * HTML文字列またはDOMノードを、アプリケーション内部で扱う標準のJSONオブジェクト形式に変換します。
 * @param {Node} node - 変換するDOMノード。
 * @returns {object | null} 変換されたJSONオブジェクト、または変換不可能な場合はnull。
 */
function domNodeToJson(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        return text ? { text: text } : null;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const jsonNode = {
        tagName: node.tagName.toLowerCase(),
        attributes: {},
        children: [],
    };

    for (const attr of node.attributes) {
        if (attr.name.toLowerCase() === 'refid') {
            jsonNode.refId = attr.value;
        } else {
            jsonNode.attributes[attr.name] = attr.value;
        }
    }

    for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent.trim();
            if (text) {
                jsonNode.text = text;
            }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childJson = domNodeToJson(child);
            if (childJson) {
                jsonNode.children.push(childJson);
            }
        }
    }

    if (jsonNode.children.length === 1 && jsonNode.children[0].text && !jsonNode.children[0].tagName) {
        jsonNode.text = jsonNode.children[0].text;
        jsonNode.children = [];
    }

    return jsonNode;
}

/**
 * AIが生成した要素オブジェクトを正規化し、アプリケーションで安全に使用できる形式に整えます。
 * 
 * 【主な役割】
 * 1. AI特有の癖（holder.jsの構文ミス、アイコン名の不備等）を自動修正。
 * 2. システム予約プロパティ（UUID等）の保護とクリーンアップ。
 * 3. 属性(attributes)のデータ構造の統一。
 * 4. 不正なdomId（ファイル名形式）に対するセキュリティガード。
 * 5. 既存の全内部ロジック（refId, templateId処理等）の完全維持。
 * 
 * @param {object | string} element - AIが生成した要素オブジェクトまたはHTML文字列。
 * @returns {object | null} 正規化された要素オブジェクト、またはnull。
 */
function normalizeElement(element) {
    // --- 1. 入力形式の統一 ---
    if (typeof element === 'string') {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(element.trim(), 'text/html');

            // 最初の一つだけでなく、すべての子要素を確認する
            const children = Array.from(doc.body.children);

            if (children.length > 1) {
                // もし複数の要素がある場合、この関数は「一つの要素」を返す前提なので
                // AgentController側で分割して処理されるべきであることをログに残す
                console.warn(`[CommandEngine] normalizeElement received multiple tags. Only the first one will be processed here. Please use multiple commands for siblings.`, element);
            }

            const parsedElement = children[0];
            if (parsedElement) {
                element = domNodeToJson(parsedElement);
            } else { return null; }
        } catch (e) {
            console.error("[CommandEngine] Error parsing element string:", e);
            return null;
        }
    }

    if (!element || !element.tagName) return null;

    // --- 2. セキュリティガード：システム内部ID(UUID)の保護 ---
    // AIが誤ってトップレベルに 'id' を含めてきた場合、システムのUUID管理と衝突するため強制削除する
    if (element.id) {
        console.warn(`[CommandEngine Sanitize] AI illegally provided a top-level 'id' (UUID). Removed. Tag: <${element.tagName}>`);
        delete element.id;
    }

    // --- 3. 属性オブジェクト(attributes)の正規化 ---
    let attributes = element.attributes || {};

    // attributesが配列 [{name: "...", value: "..."}] の場合、オブジェクト形式へ集約する
    if (Array.isArray(attributes)) {
        attributes = attributes.reduce((acc, attr) => {
            if (typeof attr === 'object' && attr !== null) {
                if (attr.name && attr.value !== undefined) {
                    if (typeof attr.value !== 'object') {
                        acc[attr.name] = attr.value;
                    }
                } else {
                    // name/value形式でないオブジェクトが含まれる場合の救済措置
                    Object.entries(attr).forEach(([key, val]) => {
                        if (typeof val !== 'object') acc[key] = val;
                    });
                }
            }
            return acc;
        }, {});
    } else if (typeof attributes === 'object' && attributes !== null) {
        // オブジェクト形式の場合、値がオブジェクト（不正なネスト）のものは除外する
        const cleanAttributes = {};
        Object.entries(attributes).forEach(([key, val]) => {
            if (typeof val !== 'object' && val !== null && val !== undefined) {
                cleanAttributes[key] = val;
            }
        });
        attributes = cleanAttributes;
    } else {
        attributes = {};
    }

    // --- 4. 属性値の自動補正 (AIハルシネーション対策) ---
    Object.keys(attributes).forEach(key => {
        let val = attributes[key];

        // A. holder.js の自動構文補正 (Zero-Cost Correction)
        // AIがよく間違える「サイズ指定のx忘れ」「禁止文字%」「改行のスペース忘れ」を自動で直す
        if (typeof val === 'string' && val.includes('holder.js/')) {
            // ① 具体的な数値指定（例: 400x300, 50x300）を補正 (px指定を維持・補完)
            val = val.replace(/(holder\.js\/)(\d+)x(\d+)/g, (match, prefix, w, h) => {
                const width = parseInt(w, 10);
                const finalWidth = width > 100 ? 100 : width;
                return `${prefix}${finalWidth}px${h}`;
            });

            // ② 100p200 -> 100px200 (幅指定の後のxを補完)
            val = val.replace(/(holder\.js\/\d+p)(\d+)/g, '$1x$2');

            // ③ 100%x200 -> 100px200 (禁止されている半角%を削除してpに合わせる)
            val = val.replace(/(\d+)%x/g, '$1px');

            // ④ textパラメータ内のサニタイズ
            if (val.includes('text=')) {
                let parts = val.split('?');
                let baseUrl = parts[0];
                let queryString = parts[1] || "";
                if (queryString) {
                    let params = queryString.split('&');
                    params = params.map(p => {
                        if (p.startsWith('text=')) {
                            let t = p.replace('text=', '');
                            // '+' をスペースに戻し、デコードする
                            try { t = decodeURIComponent(t.replace(/\+/g, ' ')); } catch (e) { t = t.replace(/\+/g, ' '); }
                            // クラッシュ防止: 半角'%'を全角'％'へ置換
                            t = t.replace(/%/g, '％');
                            // レンダリング保証: \n の前後にスペースを強制付与
                            t = t.replace(/\s*\\n\s*/g, ' \\n ');
                            return 'text=' + t;
                        }
                        return p;
                    });
                    val = baseUrl + '?' + params.join('&');
                }
            }
            attributes[key] = val;
        }

        // B. アイコン名の補正 (FontAwesomeプレフィックスの保証)
        if (key.toLowerCase() === 'icon') {
            let iconVal = String(val).trim();
            const faMatch = iconVal.match(/fa-[a-z0-9\-]+/);
            if (faMatch) {
                attributes[key] = faMatch[0]; // fa-xxx 形式をそのまま採用
            } else {
                // md- や ion- プレフィックスを除去し、強制的に fa- を付与する
                iconVal = iconVal.replace(/^(md|ion)-/, '');
                attributes[key] = `fa-${iconVal}`;
            }
        }

        // C. 属性内の 'id' プロパティの救済
        // AIが attributes.id に書いたものは domId (HTML id属性) と解釈し、UUIDでなければ引き継ぐ
        if (key.toLowerCase() === 'id') {
            if (typeof val === 'string' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)) {
                if (!element.domId) element.domId = val;
            }
            delete attributes[key]; // attributesオブジェクトからは削除
        }

        // インライン style 属性へのAIの全角％ハルシネーション対策
        if (key.toLowerCase() === 'style' && typeof val === 'string') {
            attributes[key] = val.replace(/％/g, '%');
        }
    });

    // --- 5. explain属性の移動 ---
    // 説明文属性を attributes からトップレベルプロパティへ引き上げる
    if (!element.explain && attributes.explain) {
        element.explain = attributes.explain;
        delete attributes.explain;
    }

    // --- 6. 正規化済みオブジェクトの初期構築と再帰処理 ---
    const normalized = {
        tagName: element.tagName.toLowerCase(),
        attributes: attributes,
        // 子要素に対しても本関数を再帰的に適用する
        children: (element.children || []).map(normalizeElement).filter(Boolean),
        explain: element.explain || '',
        type: element.type,
    };

    // --- 7. domId の確定とセキュリティバリデーション ---
    if (element.domId) {
        normalized.domId = element.domId;
    } else if (attributes.domId) {
        normalized.domId = attributes.domId;
        delete attributes.domId;
    }

    // [致命的エラーチェック] domId にファイルパス（.html, /）が含まれている場合は絶対に許可しない
    if (normalized.domId && typeof normalized.domId === 'string') {
        if (normalized.domId.includes('.html') || normalized.domId.includes('/')) {
            const errorMsg = `[FATAL] Invalid domId Format: The provided domId "${normalized.domId}" for <${normalized.tagName}> resembles a template-id or file path. Forbidden.`;
            console.error(errorMsg);
            throw new Error(errorMsg); // このエラーは AgentController のリカバリ処理をトリガーする
        }
    }

    // --- 8. 既存プロパティ (refId / template-id) の整合性維持 ---
    if (element.refId) normalized.refId = element.refId;

    // 複数の記法 (templateId, template-id) を統一して attributes['template-id'] に格納する
    if (element.templateId) {
        normalized.attributes['template-id'] = element.templateId;
    } else if (element['template-id']) {
        normalized.attributes['template-id'] = element['template-id'];
    }

    // --- 9. テキストコンテンツ (text) の処理 ---
    if (element.text !== undefined && element.text !== null) {
        const textValue = String(element.text);
        // HTMLタグが含まれている場合はテキストのみを抽出 (XSS対策の簡易版)
        if (/<[a-z][\s\S]*>/i.test(textValue)) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(textValue, 'text/html');
            normalized.text = doc.body.textContent || "";
        } else {
            normalized.text = textValue;
        }
    }

    // --- 10. 不要なプロパティの最終クリーンアップ ---
    delete normalized.attributes['xmlns']; // XML名前空間は不要
    delete normalized.attributes['data-template-id']; // 重複する旧属性の削除

    // placeholder的な '#' リンクは不要なため削除
    if (normalized.attributes.href === '#') {
        delete normalized.attributes.href;
    }

    // 防御: トップレベルに 'id' プロパティが残っていないことを最終確認
    delete normalized.id;

    return normalized;
}

/**
 * URLからリソースを取得し、SRI (Subresource Integrity) のためのハッシュ値を計算します。
 * @param {string} url - 計算対象のリソースのURL。
 * @returns {Promise<string>} 'sha384-...' 形式のハッシュ文字列、またはエラー時は空文字。
 */
async function calculateSriHash(url) {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch resource: ${response.statusText}`);
        const data = await response.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-384', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray));
        return `sha384-${hashBase64}`;
    } catch (error) { console.error(`Could not calculate SRI hash for ${url}:`, error); return ''; }
}

/**
 * HTML文字列内の `<script>` および `<link>` タグに、SRIのための `integrity` 属性と `crossorigin` 属性を自動で付与または上書きします。
 * @param {string} htmlString - 処理対象のHTML文字列。
 * @returns {Promise<string>} 処理後のHTML文字列。
 */
async function addIntegrityToTags(htmlString) {
    if (!htmlString || typeof htmlString !== 'string') return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const tags = doc.querySelectorAll('script[src], link[rel="stylesheet"][href]');
    const promises = Array.from(tags).map(async (tag) => {
        const url = tag.src || tag.href;
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            const hash = await calculateSriHash(url);
            if (hash) {
                tag.setAttribute('integrity', hash);
                tag.setAttribute('crossorigin', 'anonymous');
            } else {
                tag.removeAttribute('integrity');
                tag.removeAttribute('crossorigin');
            }
        }
    });
    await Promise.all(promises);
    return doc.head.innerHTML + doc.body.innerHTML;
}

/**
 * updateAppHeader/BodyTop/BodyBottomコマンド用の安全なtransformer関数。
 * @param {object} params - AIから渡されたコマンドのパラメータ。
 * @param {string} commandName - 現在のコマンド名（ログ出力用）。
 * @returns {Promise<string>} Reduxアクション用のペイロード（HTML文字列。
 */
async function safeHtmlTransformer(params, commandName) {
    console.log(`[CommandEngine Debug] Entering transformer for "${commandName}". Raw params:`, JSON.parse(JSON.stringify(params)));
    const htmlPayload = params.html;

    if (typeof htmlPayload !== 'string') {
        const errorMsg = `[FATAL ERROR in CommandEngine] "${commandName}" received a non-string payload. This is a critical bug. Payload type: ${typeof htmlPayload}. Payload value: ${JSON.stringify(htmlPayload)}`;
        console.error(errorMsg);
        return ''; // 安全のため空文字を返す
    }

    console.log(`[CommandEngine Debug] Payload for "${commandName}" is a valid string. Length: ${htmlPayload.length}.`);
    const processedHtml = await addIntegrityToTags(htmlPayload);
    console.log(`[CommandEngine Debug] HTML after addIntegrityToTags for "${commandName}". Length: ${processedHtml.length}.`);
    return processedHtml;
}

const commandMap = {
    'addPage': { action: addTopLevelPage, dispatch: () => storeDispatch, transformer: (p) => ({ newTemplate: normalizeElement(p.element) }) },
    'addElementAsFirstChild': { action: insertAsChild, dispatch: () => storeDispatch, transformer: (p) => ({ targetId: p.targetId, newElement: normalizeElement(p.element) }) },
    'addElementAsLastChild': { action: insertAsLastChild, dispatch: () => storeDispatch, transformer: (p) => ({ targetId: p.targetId, newElement: normalizeElement(p.element) }) },
    'addElementAfter': { action: insertAfter, dispatch: () => storeDispatch, transformer: (p) => ({ targetId: p.targetId, newElement: normalizeElement(p.element) }) },
    'updateAttribute': {
        isComplex: true, // 内部ロジックを持つため isComplex に変更
        handler: (p) => {
            const attributeName = p.attribute.toLowerCase();
            // 値がオブジェクトの場合は実行しない
            if (typeof p.value === 'object' && p.value !== null) {
                console.warn(`[CommandEngine] Skipped updateAttribute for "${attributeName}" because value is an object.`, p.value);
                return;
            }
            // システム内部IDである 'id' 属性の変更は警告の上、コマンドをスキップ
            if (attributeName === 'id' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.value)) {
                console.warn(
                    `[CommandEngine Guard] AI attempted to modify the protected system 'id' attribute.` +
                    ` This action is forbidden and was skipped. Target: ${p.targetId}`
                );
                return; // 処理を中止して次のコマンドへ
            }

            // AIが 'id' を指定してきた場合、それはHTMLのid属性（=domId）の変更を意図していると解釈する
            const attributeType = attributeName === 'id' ? 'domId' : p.attribute;

            // style属性更新時の全角％サニタイズ
            let value = p.value;
            if (attributeName === 'style' && typeof value === 'string') {
                value = value.replace(/％/g, '%');
            }

            const payload = {
                elementId: p.targetId,
                attributeType: attributeType,
                newValue: value,
                inputType: 'text'
            };
            storeDispatch(updateElementAttribute(payload));
        }
    },
    'deleteElement': {
        isComplex: true,
        handler: (p) => {
            // executeSingleCommandでtargetIdが解決されているので、p.targetIdが存在するはず
            if (!p.targetId) {
                throw new Error(`deleteElement command failed: Target element could not be resolved from params ${JSON.stringify(p)}`);
            }
            const rootElement = templateJsonStore.getState().templateJson.templates[0];
            // 念のためルート要素が存在するかチェック
            if (!rootElement) {
                // ルート要素がない異常事態。削除処理を安全のために停止。
                throw new Error(`deleteElement command failed: Could not find the application root element to perform a safety check.`);
            } else if (p.targetId === rootElement.id) {
                const errorMessage = `SECURITY VIOLATION: Attempt to delete the application root element (domId: '${rootElement.domId}') was blocked. This action is absolutely forbidden.`;
                console.error(errorMessage);
                // AgentControllerがエラーを補足して回復を試みるように、エラーをスローする
                throw new Error(errorMessage);
            }
            // 問題なければ、本来のアクションを実行
            storeDispatch(moveToTrashbox({ targetId: p.targetId }));
        }
    },
    'deleteElementPermanently': {
        isComplex: true,
        handler: (p) => {
            if (!p.targetId) {
                throw new Error(`deleteElementPermanently command failed: Target element could not be resolved from params ${JSON.stringify(p)}`);
            }
            const rootElement = templateJsonStore.getState().templateJson.templates[0];
            if (!rootElement) {
                throw new Error(`deleteElementPermanently command failed: Could not find the application root element to perform a safety check.`);
            } else if (p.targetId === rootElement.id) {
                const errorMessage = `SECURITY VIOLATION: Attempt to delete the application root element (domId: '${rootElement.domId}') was blocked. This action is absolutely forbidden.`;
                console.error(errorMessage);
                throw new Error(errorMessage);
            }
            storeDispatch(deleteElementPermanently({ targetId: p.targetId }));
        }
    },
    'moveElementAfter': { action: moveAfter, dispatch: () => storeDispatch, transformer: (p) => ({ destId: p.targetId, srcId: p.elementId }) },
    'moveElementAsFirstChild': { action: moveAsChild, dispatch: () => storeDispatch, transformer: (p) => ({ destId: p.targetId, srcId: p.elementId }) },
    'moveElementAsLastChild': { action: moveAsLastChild, dispatch: () => storeDispatch, transformer: (p) => ({ destId: p.targetId, srcId: p.elementId }) },
    'upsertScript': {
        isComplex: true,
        handler: (p) => {
            const scriptCodeArray = Array.isArray(p.scriptContent) ? p.scriptContent : String(p.scriptContent).split('\n');
            const scriptCodeString = scriptCodeArray.join('\n');
            const existingScript = selectScriptByName(scriptItemStore.getState(), p.scriptName);

            // 1. スクリプトの保存
            if (existingScript) {
                scriptDispatch(updateScriptItem({
                    originScriptName: p.scriptName,
                    scriptValue: scriptCodeArray
                }));
            } else {
                scriptDispatch(addNewScript({
                    scriptName: p.scriptName,
                    scriptContent: scriptCodeArray
                }));
            }

            // 2. Webコンポーネントとしての解析とToolBox等への自動登録
            try {
                const definitions = findCustomElementDefinitions(scriptCodeString);
                const allDeclarations = extractDeclarationInfo(scriptCodeString);
                const scriptName = p.scriptName;

                // 依存する古いアイテムを一度クリーンアップ
                toolBoxDispatch(removeUserScriptComponentsByScriptName({ scriptName }));
                attributeDispatch(removeAttributesByScript({ originScriptName: scriptName }));
                eventDispatch(removeEventsByScript({ originScriptName: scriptName }));

                if (definitions.length > 0) {
                    definitions.forEach(def => {
                        const { tagName, className, jsdoc } = def;
                        const componentComment = allDeclarations[className]?.comment || p.comment || "";

                        // ToolBoxへの登録
                        toolBoxDispatch(syncUserScriptComponent({
                            originScriptName: scriptName,
                            tagName: tagName,
                            comment: componentComment,
                        }));

                        // Attribute / Event の登録処理
                        const idTypeComment = `/** @type{${className}} */`;
                        attributeDispatch(setIdTypeCommentForTag({ tagName, idTypeComment }));

                        const { attributes, events } = parseJSDoc(jsdoc);

                        // 属性の追加
                        const existingAttributes = selectAttributeByTagName(attributeItemStore.getState(), tagName);
                        attributes.forEach(attr => {
                            if (!existingAttributes.some(ex => Object.keys(ex)[0] === attr.name)) {
                                const attributePayload = { [attr.name]: { inputName: attr.inputName, inputType: attr.inputType, options: attr.options, comment: attr.comment, registerType: 'customScript', originScriptName: scriptName } };
                                const tagExists = attributeItemStore.getState().attributeItems.items.some(i => i.tagName === tagName);
                                if (!tagExists) attributeDispatch(addNewAttributeTag({ tagName, attributes: [] }));
                                attributeDispatch(addAttributeToExistingTag({ tagName, attribute: attributePayload }));
                            }
                        });

                        // イベントの追加
                        const existingEvents = selectEventByTagName(eventItemStore.getState(), tagName);
                        events.forEach(event => {
                            if (!existingEvents.some(ex => Object.keys(ex)[0] === event.name)) {
                                const eventPayload = { [event.name]: { listenerTarget: "element", scripts: [], comment: event.comment, registerType: 'customScript', originScriptName: scriptName } };
                                const tagExists = eventItemStore.getState().eventItems.items.some(i => i.tagName === tagName);
                                if (!tagExists) eventDispatch(addNewEventTag({ tagName, events: [] }));
                                eventDispatch(addEventToExistingTag({ tagName, event: eventPayload }));
                            }
                        });
                    });
                }
            } catch (e) {
                console.error(`[CommandEngine] Error parsing Web Components from script "${p.scriptName}":`, e);
            }
        }
    },
    'deleteScript': {
        isComplex: true,
        handler: (p) => {
            if (p.scriptName) {
                // スクリプト削除時にもToolBox, Attributeの紐付けを解除する
                toolBoxDispatch(removeUserScriptComponentsByScriptName({ scriptName: p.scriptName }));
                attributeDispatch(removeAttributesByScript({ originScriptName: p.scriptName }));

                // イベントの紐付け解除
                eventDispatch(removeEventsByScript({ originScriptName: p.scriptName }));
                scriptDispatch(deleteScriptPermanently(p.scriptName));
            }
        }
    },
    'addScriptToEvent': {
        isComplex: true,
        handler: (p) => {
            const state = templateJsonStore.getState().templateJson;
            const element = p.targetId ? findElementById(state, p.targetId) : findElementByDomId(state, p.targetDomId);

            if (!element) return;
            const { tagName } = element;
            const { eventType, scriptName } = p;

            const currentEventItems = eventItemStore.getState().eventItems.items;
            const targetTagDef = currentEventItems.find(item => item.tagName === tagName);
            if (targetTagDef && targetTagDef.events) {
                const targetEventDef = targetTagDef.events.find(e => e[eventType]);
                if (targetEventDef && targetEventDef[eventType].scripts?.some(s => s.scriptName === scriptName && s.templateId === element.id)) return;
            }

            commandMap.addEventDefinition.handler({ tagName, eventType, comment: `Auto-defined` });
            eventDispatch(addScriptToEvent({ tagName, templateId: element.id, eventName: eventType, scriptName }));
        },
        precheck: (params) => {

            if (!params.scriptName) {
                throw new Error(`[Execution Error] addScriptToEvent failed: Missing required parameter "scriptName". You provided: ${JSON.stringify(params)}`);
            }

            const exists = scriptItemStore.getState().scriptItems.scripts.some(s => s.scriptName === params.scriptName);
            if (!exists) {
                throw new Error(`[Execution Error] addScriptToEvent failed: Script "${params.scriptName}" does not exist. Please ensure the script is created using 'upsertScript' with this exact name before binding it.`);
            }
            return true;
        },
    },
    'removeScriptFromEvent': { action: removeScriptFromEvent, dispatch: () => eventDispatch, transformer: (p) => { let e = p.targetId ? findElementInStoreById(p.targetId) : findElementByDomId(templateJsonStore.getState().templateJson, p.targetDomId); if (!e) throw new Error(`Element not found: ${p.targetId || p.targetDomId}`); return { tagName: e.tagName, templateId: e.id, eventName: p.eventType, scriptName: p.scriptName }; } },
    'addEventDefinition': {
        isComplex: true,
        handler: (p) => {
            const { tagName, eventType, comment } = p;
            const state = eventItemStore.getState().eventItems;
            const tag = state.items.find(item => item.tagName === tagName);

            if (tag) {
                // タグ定義が既に存在する場合
                const eventExists = tag.events.some(e => Object.keys(e)[0] === eventType);
                if (eventExists) {
                    // イベント定義も既に存在する場合は何もしない（重複スキップ）
                    console.info(`[CommandEngine] Skipped addEventDefinition: Event "${eventType}" for tag "${tagName}" already exists.`);
                } else {
                    // イベント定義だけを追加する
                    eventDispatch(addEventToExistingTag({
                        tagName: tagName,
                        event: {
                            [eventType]: {
                                listenerTarget: "element",
                                scripts: [],
                                comment: comment || `${eventType} event`
                            }
                        }
                    }));
                }
            } else {
                // タグ定義が存在しない場合、まずタグ定義を作成してからイベント定義を追加する
                eventDispatch(addNewEventTag({ tagName }));
                eventDispatch(addEventToExistingTag({
                    tagName: tagName,
                    event: {
                        [eventType]: {
                            listenerTarget: "element",
                            scripts: [],
                            comment: comment || `${eventType} event`
                        }
                    }
                }));
            }
        }
    },
    'removeEventDefinition': { action: removeEventDefinition, dispatch: () => eventDispatch, transformer: (p) => ({ tagName: p.tagName, eventName: p.eventType }) },

    'addScriptToServiceWorkerEvent': {
        action: addScriptToServiceWorkerEvent,
        dispatch: () => serviceWorkerDispatch,
        isComplex: true,
        handler: (p) => {
            const { eventName, scriptName, comment } = p;

            // 1. イベント定義が存在するかチェック
            const currentItems = serviceworkerItemStore.getState().serviceworkerItems.items;
            const exists = currentItems.some(item => item.eventName === eventName);

            // 2. 存在しない場合は作成 (UPSERT)
            if (!exists) {
                console.log(`[CommandEngine] Auto-creating Service Worker event definition for: ${eventName}`);
                serviceWorkerDispatch(upsertServiceWorkerEvent({
                    eventName: eventName,
                    comment: comment || `Auto-generated for ${scriptName}`,
                    scripts: [] // スクリプトは次のステップで追加
                }));
            }

            // 3. スクリプトをイベントに追加
            serviceWorkerDispatch(addScriptToServiceWorkerEvent({
                eventName: eventName,
                scriptName: scriptName
            }));
        },
        transformer: (p) => ({ eventName: p.eventName, scriptName: p.scriptName }),
        precheck: (params) => {
            const exists = scriptItemStore.getState().scriptItems.scripts.some(s => s.scriptName === params.scriptName);
            if (!exists) {
                throw new Error(`[Execution Error] addScriptToServiceWorkerEvent failed: Script "${params.scriptName}" does not exist.`);
            }
            return true;
        },
    },

    'removeScriptFromServiceWorkerEvent': {
        action: deleteServiceWorkerEventScript,
        dispatch: () => serviceWorkerDispatch,
        transformer: (p) => ({ eventName: p.eventName, scriptName: p.scriptName })
    },

    'deleteServiceWorkerEvent': {
        action: deleteServiceWorkerEvent,
        dispatch: () => serviceWorkerDispatch,
        transformer: (p) => ({ eventName: p.eventName })
    },

    'updateGlobalStyle': {
        isComplex: true,
        handler: (p) => {
            const state = templateJsonStore.getState().templateJson;
            const rootElement = state.templates[0];
            const oldCss = rootElement.attributes.style || "";

            let finalCss = "";

            // AIが誤って出力した全角％を半角に直す
            let incomingCss = p.css || "";
            incomingCss = incomingCss.replace(/％/g, '%');

            // mode: "replace" が明示されている、または既存がない場合は全置換
            // それ以外（デフォルト）は差分マージ
            if (p.mode === 'replace' || !oldCss) {
                finalCss = incomingCss;
            } else {
                // p.deleted_selectors は AI が明示的に消したいと言ったもの（オプション）
                finalCss = mergeCss(oldCss, incomingCss, p.deleted_selectors || []);
            }

            storeDispatch(updateElementAttribute({
                elementId: rootElement.id,
                attributeType: 'style',
                newValue: finalCss,
                inputType: 'textarea'
            }));
        }
    },
    // リソースの追加 (addAppResource)
    'addAppResource': {
        isComplex: true,
        handler: async (p) => {
            const { location, tagName, attributes, content } = p;
            let currentHtml = '';
            let action = null;
            const state = appSettingStore.getState().appSetting;

            if (location === 'head') {
                currentHtml = state.app_header;
                action = updateAppHeader;
            } else if (location === 'body_top') {
                currentHtml = state.body_top;
                action = updateBodyTop;
            } else if (location === 'body_bottom') {
                currentHtml = state.body_bottom;
                action = updateBodyBottom;
            } else {
                throw new Error(`Invalid location: ${location}. Must be one of: 'head', 'body_top', 'body_bottom'.`);
            }

            // HTMLオブジェクト対応 ({html: "..."} 形式の場合)
            const htmlString = (typeof currentHtml === 'object') ? (currentHtml.html || '') : currentHtml;

            // HTMLを変更
            let newHtml = modifyResourceHtml(htmlString, 'add', { tagName, attributes, content });

            // SRIハッシュ付与 (セキュリティ強化)
            newHtml = await addIntegrityToTags(newHtml);

            appSettingDispatch(action(newHtml));
        }
    },

    // リソースの削除 (removeAppResource)
    'removeAppResource': {
        isComplex: true,
        handler: async (p) => {
            const { location, selector } = p;
            let currentHtml = '';
            let action = null;
            const state = appSettingStore.getState().appSetting;

            if (location === 'head') {
                currentHtml = state.app_header;
                action = updateAppHeader;
            } else if (location === 'body_top') {
                currentHtml = state.body_top;
                action = updateBodyTop;
            } else if (location === 'body_bottom') {
                currentHtml = state.body_bottom;
                action = updateBodyBottom;
            } else {
                throw new Error(`Invalid location: ${location}`);
            }

            const htmlString = (typeof currentHtml === 'object') ? (currentHtml.html || '') : currentHtml;

            // HTMLを変更 (内部で PROTECTED_RESOURCES チェックが走る)
            const newHtml = modifyResourceHtml(htmlString, 'remove', { selector });

            appSettingDispatch(action(newHtml));
        }
    },

    'updateProjectSpecification': {
        action: updateProjectSpecification,
        dispatch: () => appSettingDispatch,
        transformer: (p) => p.specification
    },

    // --- [DEPRECATED] 廃止された破壊的コマンド ---
    // これらは古いプロンプトや履歴からの再実行で呼ばれる可能性があるため、
    // マップから削除せず、実行時に「安全なコマンドを使ってください」とエラーを返すようにする。
    // これによりAIはエラーメッセージを読み、addAppResource/removeAppResourceを使って再試行できる。
    'updateAppHeader': {
        isComplex: true,
        handler: () => {
            throw new Error(
                "[Security Alert] The 'updateAppHeader' command is deprecated because it overwrites the entire header, which is dangerous. " +
                "Please use 'addAppResource' to add elements or 'removeAppResource' to remove specific elements instead."
            );
        }
    },
    'updateBodyTop': {
        isComplex: true,
        handler: () => {
            throw new Error(
                "[Security Alert] 'updateBodyTop' is deprecated. Use 'addAppResource' or 'removeAppResource' instead."
            );
        }
    },
    'updateBodyBottom': {
        isComplex: true,
        handler: () => {
            throw new Error(
                "[Security Alert] 'updateBodyBottom' is deprecated. Use 'addAppResource' or 'removeAppResource' instead."
            );
        }
    },

    'refreshPreview': { action: setReloadOn, dispatch: () => storeDispatch, transformer: () => ({}) },

    'deletePage': {
        isComplex: true,
        handler: (p) => {
            const { templateId } = p;
            storeDispatch(deleteTemplateByAttributeId({ templateId: templateId }));
        }
    },

    // ページのUUIDを変えずに中身だけ入れ替えるコマンド
    'updatePageContent': {
        action: replaceTemplateChildren,
        dispatch: () => storeDispatch,
        transformer: (p) => ({
            targetId: p.targetId,
            newElement: normalizeElement(p.newContent)
        })
    },

    'finish': { isComplex: true, handler: () => { } }
};

async function executeSingleCommand(commandObject, currentState) {
    let flatParams = {};
    if (commandObject.params) {
        flatParams = { ...commandObject.params };

        // UI構築系
        if (flatParams.ui_element) { flatParams.element = flatParams.ui_element; }

        // 要素移動系
        if (flatParams.move_elementId) { flatParams.elementId = flatParams.move_elementId; }
        if (flatParams.move_elementDomId) { flatParams.elementDomId = flatParams.move_elementDomId; }

        // 属性更新系
        if (flatParams.attr_name) { flatParams.attribute = flatParams.attr_name; }
        if (flatParams.attr_value) { flatParams.value = flatParams.attr_value; }

        // スクリプト系
        if (flatParams.script_name) { flatParams.scriptName = flatParams.script_name; }
        if (flatParams.script_content) { flatParams.scriptContent = flatParams.script_content; }
        if (flatParams.script_comment) { flatParams.comment = flatParams.script_comment; }

        // UIイベント系
        if (flatParams.event_type) { flatParams.eventType = flatParams.event_type; }

        // Service Worker系
        if (flatParams.sw_event_name) { flatParams.eventName = flatParams.sw_event_name; }

        // スタイル系
        if (flatParams.style_css) { flatParams.css = flatParams.style_css; }

        // リソース管理系
        if (flatParams.resource_location) { flatParams.location = flatParams.resource_location; }
        if (flatParams.resource_tagName) { flatParams.tagName = flatParams.resource_tagName; }
        if (flatParams.resource_attributes) { flatParams.attributes = flatParams.resource_attributes; }
        if (flatParams.resource_content) { flatParams.content = flatParams.resource_content; }
        if (flatParams.resource_selector) { flatParams.selector = flatParams.resource_selector; }

    } else {
        // paramsがない古い形式への後方互換性
        flatParams = { ...commandObject };
        delete flatParams.command;
    }

    // --- 2. パラメータ自動修復 (AIのハルシネーション対策) ---
    // AIが 'element' (ui_element) オブジェクトを作らずに、tagNameなどをフラットに記述してしまった場合の救済措置
    const uiConstructionCommands = ['addPage', 'addElementAsFirstChild', 'addElementAsLastChild', 'addElementAfter'];
    if (uiConstructionCommands.includes(commandObject.command)) {
        if (!flatParams.element && flatParams.tagName) {
            console.warn(`[CommandEngine] 'element' object missing for command "${commandObject.command}". Attempting to auto-construct element from flat params.`, flatParams);

            flatParams.element = {
                tagName: flatParams.tagName,
                // domIdは elementDomId, domId, id の順で探す
                domId: flatParams.elementDomId || flatParams.domId || flatParams.id,
                attributes: flatParams.attributes || [],
                text: flatParams.text || flatParams.content,
                explain: flatParams.explain,
                children: flatParams.children || []
            };

            // attributesが配列でない場合の変換
            if (flatParams.attributes && !Array.isArray(flatParams.attributes) && typeof flatParams.attributes === 'object') {
                flatParams.element.attributes = Object.entries(flatParams.attributes).map(([name, value]) => ({ name, value }));
            }
        }
    }

    const { command } = commandObject;
    const params = flatParams;

    if (!command) throw new Error("不正なコマンド形式です。'command' がありません。");

    const commandConfig = commandMap[command];
    if (!commandConfig) throw new Error(`コマンド "${command}" は存在しません。`);

    const resolvedParams = { ...params };

    // targetDomId が指定されているが targetId がまだない場合
    if (params.targetDomId && !params.targetId) {
        // 1. まず domId として検索
        // currentState.jsonState は { templates: [...], trashbox: [...] } を持つ想定
        // Sliceの関数は state.templates を参照するので、currentState.jsonState をそのまま渡せばOK
        let target = findElementByDomId(currentState.jsonState, params.targetDomId);

        // 2. 見つからない場合、UUID (ID) として検索 (AIがUUIDを渡してきた場合のフォールバック)
        if (!target) {
            target = findElementById(currentState.jsonState, params.targetDomId);
            if (target) {
                console.warn(`[CommandEngine] Resolved target by UUID instead of domId. AI passed UUID "${params.targetDomId}" as targetDomId.`);
            }
        }

        if (target) {
            resolvedParams.targetId = target.id;
        } else {
            // targetDomIdが見つからない場合は警告 (targetIdが必須でないコマンドもあるためエラーにはしない)
            console.warn(`[CommandEngine] targetDomId "${params.targetDomId}" not found for command "${command}".`);
        }
    }

    // 事前チェック (Precheck)
    if (commandConfig.precheck) {
        try {
            if (!commandConfig.precheck(resolvedParams, currentState)) {
                return {
                    success: true,
                    message: `コマンド "${command}" は重複または無効なためスキップされました。`,
                    data: { newState: currentState.jsonState }
                };
            }
        } catch (e) {
            console.error(`%c[CommandEngine] Precheck Critical Error in "${command}"`, 'color: red; font-weight: bold; font-size: 1.2em;');
            console.error(`Details:`, e);
            console.error(`Params:`, resolvedParams);
            throw e;
        }
    }

    // 複雑なロジックを持つコマンド (Handler) の実行
    if (command === 'finish' || commandConfig.isComplex) {
        try {
            await commandConfig.handler(resolvedParams);
            return {
                success: true,
                message: `コマンド "${command}" を実行しました。`,
                data: { newState: templateJsonStore.getState().templateJson }
            };
        } catch (e) {
            console.error(`[CommandEngine] Handler execution failed for "${command}".`);
            console.error(`[CommandEngine] Error details:`, e);
            console.error(`[CommandEngine] Failed Params:`, resolvedParams);
            throw new Error(`コマンド "${command}" の実行中にエラーが発生しました: ${e.message}`);
        }
    }

    // シンプルなRedux Actionをdispatchするコマンドの処理
    const storeUpdatePromise = new Promise(resolve => {
        const dispatchFunc = commandConfig.dispatch();
        let targetStore = templateJsonStore;
        // ストアの判定
        if (dispatchFunc === scriptDispatch) targetStore = scriptItemStore;
        else if (dispatchFunc === eventDispatch) targetStore = eventItemStore;
        else if (dispatchFunc === appSettingDispatch) targetStore = appSettingStore;
        else if (dispatchFunc === serviceWorkerDispatch) targetStore = serviceworkerItemStore;
        else if (dispatchFunc === attributeDispatch) targetStore = attributeItemStore;
        else if (dispatchFunc === toolBoxDispatch) targetStore = toolBoxItemStore;

        const unsubscribe = targetStore.subscribe(() => {
            unsubscribe();
            resolve(targetStore.getState());
        });

        // Redux Actionの実行 (Transformerを経てPayload生成)
        try {
            // パラメータ変換 (Transformer)
            const payload = commandConfig.transformer(resolvedParams);
            console.log(`[CommandEngine] Dispatching action for "${command}". Payload:`, payload);
            dispatchFunc(commandConfig.action(payload));
        } catch (e) {
            console.error(`[CommandEngine] Transformation or Dispatch failed for "${command}".`, e);
            throw e; // Promiseの中で投げるとcatchされない恐れがあるが、外側のtry-catchはないのでここでthrow
        }
    });

    const updatedStateContainer = await storeUpdatePromise;

    // 更新後の状態を取得
    let finalState;
    if (updatedStateContainer.templateJson) {
        finalState = updatedStateContainer.templateJson;
    } else {
        finalState = currentState.jsonState;
    }

    return {
        success: true,
        message: `コマンド "${command}" を実行しました。`,
        data: { newState: finalState }
    };
}

export async function executeCommandList(commandList, initialJsonState) {
    let currentState = initialJsonState;
    const results = [];
    storeDispatch(setReloadOff());
    try {
        for (const commandObject of commandList) {
            console.groupCollapsed(`[CommandEngine] Exec: ${commandObject.command}`);
            try {
                // ID解決の前処理 (Slice関数を利用)
                const params = commandObject.params || {};
                if (params.targetId) {
                    // targetIdで探して見つからなければ、それはdomIdかもしれないのでdomIdで探してUUIDに変換する
                    if (!findElementById(currentState, params.targetId)) {
                        const targetByDomId = findElementByDomId(currentState, params.targetId);
                        if (targetByDomId) {
                            (commandObject.params || {}).targetId = targetByDomId.id;
                        }
                    }
                }

                const result = await executeSingleCommand(commandObject, { jsonState: currentState });
                currentState = result.data.newState;
                results.push(result);
            } catch (error) {
                console.error(error);
                error.failedCommand = commandObject;
                error.resultsSoFar = results;
                throw error;
            } finally {
                console.groupEnd();
            }
        }
    } finally {
        storeDispatch(setReloadOn());
    }
    return results;
}